/**
 * Browser Notification API — opt-in helper.
 *
 * Behaviour:
 *  - First call: triggers the permission prompt (user gesture not required by
 *    the spec, but most browsers honour it on a recent user interaction).
 *  - Subsequent calls: only show a notification when the tab is hidden or
 *    unfocused; otherwise the in-app toast is enough and a duplicate OS
 *    notification would be noisy.
 *
 * No external store; permission state is read fresh from `Notification.permission`.
 */

let promptInFlight: Promise<NotificationPermission> | null = null;

export function isBrowserNotificationSupported(): boolean {
  return typeof window !== "undefined" && "Notification" in window;
}

export async function ensureBrowserNotificationPermission(): Promise<NotificationPermission> {
  if (!isBrowserNotificationSupported()) return "denied";
  if (Notification.permission === "granted" || Notification.permission === "denied") {
    return Notification.permission;
  }
  if (promptInFlight) return promptInFlight;
  promptInFlight = Notification.requestPermission().finally(() => {
    promptInFlight = null;
  });
  return promptInFlight;
}

interface NotifyArgs {
  title: string;
  body?: string;
  /** When the user clicks the notification. Default: focus the window. */
  onClick?: () => void;
  /** Tag groups consecutive notifications so they replace each other instead of stacking. */
  tag?: string;
}

export async function showBrowserNotification({ title, body, onClick, tag }: NotifyArgs) {
  if (!isBrowserNotificationSupported()) return;
  // Don't spam when the user is already looking at the tab — toast suffices.
  const visible = typeof document !== "undefined"
    && document.visibilityState === "visible"
    && document.hasFocus();
  if (visible) return;

  const perm = await ensureBrowserNotificationPermission();
  if (perm !== "granted") return;

  try {
    const n = new Notification(title, {
      body: body || "",
      tag: tag || "erp-chat",
      // No icon path bundled — the OS uses the favicon.
    });
    if (onClick) {
      n.onclick = (e) => {
        e.preventDefault();
        try { window.focus(); } catch { /* ignore */ }
        try { onClick(); } catch { /* ignore */ }
        n.close();
      };
    }
  } catch {
    // Notifications can throw on iOS / older browsers — silently degrade.
  }
}
